// Shared API utilities
import { API_ENDPOINTS } from './constants';
import { API_BASE } from '@/config/api';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface ApiError {
  message: string;
  status: number;
  code?: string;
}

/**
 * Authorization header for components that call `fetch` directly.
 * Returns an empty object when signed out, so it can be spread unconditionally.
 */
export const getAuthHeaders = (): Record<string, string> => {
  const token = typeof window === 'undefined' ? null : localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export class ApiClient {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;

  constructor(baseURL: string = API_BASE) {

    this.baseURL = baseURL;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    };
  }

  private getAuthToken(): string | null {
    return localStorage.getItem('authToken');
  }

  private getHeaders(customHeaders?: Record<string, string>): Record<string, string> {
    const token = this.getAuthToken();
    const headers = { ...this.defaultHeaders, ...customHeaders };
    
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    
    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    try {
      const data = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          error: data.message || `HTTP ${response.status}: ${response.statusText}`,
        };
      }
      
      return {
        success: true,
        data: data.data || data,
        message: data.message,
      };
    } catch (_error) {
      return {
        success: false,
        error: 'Failed to parse response',
      };
    }
  }

  async get<T>(endpoint: string, customHeaders?: Record<string, string>): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'GET',
        headers: this.getHeaders(customHeaders),
      });
      
      return this.handleResponse<T>(response);
    } catch (_error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  async post<T>(endpoint: string, data?: any, customHeaders?: Record<string, string>): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'POST',
        headers: this.getHeaders(customHeaders),
        body: data ? JSON.stringify(data) : undefined,
      });
      
      return this.handleResponse<T>(response);
    } catch (_error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  async put<T>(endpoint: string, data?: any, customHeaders?: Record<string, string>): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'PUT',
        headers: this.getHeaders(customHeaders),
        body: data ? JSON.stringify(data) : undefined,
      });
      
      return this.handleResponse<T>(response);
    } catch (_error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  async delete<T>(endpoint: string, customHeaders?: Record<string, string>): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'DELETE',
        headers: this.getHeaders(customHeaders),
      });
      
      return this.handleResponse<T>(response);
    } catch (_error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  async upload<T>(endpoint: string, formData: FormData): Promise<ApiResponse<T>> {
    try {
      const token = this.getAuthToken();
      const headers: Record<string, string> = {};
      
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'POST',
        headers,
        body: formData,
      });
      
      return this.handleResponse<T>(response);
    } catch (_error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }
}

// Create default API client instance
export const apiClient = new ApiClient();

// Helper functions for common API operations
export const api = {
  // Auth endpoints
  auth: {
    login: (credentials: { email: string; password: string }) =>
      apiClient.post(API_ENDPOINTS.AUTH.LOGIN, credentials),
    
    register: (userData: any) =>
      apiClient.post(API_ENDPOINTS.AUTH.REGISTER, userData),
    
    logout: () =>
      apiClient.post(API_ENDPOINTS.AUTH.LOGOUT),
    
    getProfile: () =>
      apiClient.get(API_ENDPOINTS.AUTH.PROFILE),
    
    updateProfile: (profileData: any) =>
      apiClient.put(API_ENDPOINTS.AUTH.PROFILE, profileData),
  },

  // Order endpoints
  orders: {
    list: (params?: Record<string, any>) => {
      const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
      return apiClient.get(`${API_ENDPOINTS.ORDERS.LIST}${queryString}`);
    },
    
    create: (orderData: any) =>
      apiClient.post(API_ENDPOINTS.ORDERS.CREATE, orderData),
    
    update: (id: string, orderData: any) =>
      apiClient.put(API_ENDPOINTS.ORDERS.UPDATE.replace(':id', id), orderData),
    
    delete: (id: string) =>
      apiClient.delete(API_ENDPOINTS.ORDERS.DELETE.replace(':id', id)),
    
    track: (phone: string) =>
      apiClient.get(`${API_ENDPOINTS.ORDERS.TRACK}?phone=${encodeURIComponent(phone)}`),
  },

  // Measurement endpoints
  measurements: {
    list: (params?: Record<string, any>) => {
      const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
      return apiClient.get(`${API_ENDPOINTS.MEASUREMENTS.LIST}${queryString}`);
    },
    
    create: (measurementData: any) =>
      apiClient.post(API_ENDPOINTS.MEASUREMENTS.CREATE, measurementData),
    
    update: (id: string, measurementData: any) =>
      apiClient.put(API_ENDPOINTS.MEASUREMENTS.UPDATE.replace(':id', id), measurementData),
    
    delete: (id: string) =>
      apiClient.delete(API_ENDPOINTS.MEASUREMENTS.DELETE.replace(':id', id)),
  },

  // Customer endpoints
  customers: {
    list: (params?: Record<string, any>) => {
      const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
      return apiClient.get(`${API_ENDPOINTS.CUSTOMERS.LIST}${queryString}`);
    },
    
    create: (customerData: any) =>
      apiClient.post(API_ENDPOINTS.CUSTOMERS.CREATE, customerData),
    
    update: (id: string, customerData: any) =>
      apiClient.put(API_ENDPOINTS.CUSTOMERS.UPDATE.replace(':id', id), customerData),
    
    delete: (id: string) =>
      apiClient.delete(API_ENDPOINTS.CUSTOMERS.DELETE.replace(':id', id)),
  },

  // Business endpoints
  business: {
    getProfile: () =>
      apiClient.get(API_ENDPOINTS.BUSINESS.PROFILE),
    
    updateProfile: (profileData: any) =>
      apiClient.put(API_ENDPOINTS.BUSINESS.PROFILE, profileData),
    
    getAnalytics: (params?: Record<string, any>) => {
      const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
      return apiClient.get(`${API_ENDPOINTS.BUSINESS.ANALYTICS}${queryString}`);
    },
    
    getCustomers: (params?: Record<string, any>) => {
      const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
      return apiClient.get(`${API_ENDPOINTS.BUSINESS.CUSTOMERS}${queryString}`);
    },
  },

  // Admin endpoints
  admin: {
    getUsers: (params?: Record<string, any>) => {
      const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
      return apiClient.get(`${API_ENDPOINTS.ADMIN.USERS}${queryString}`);
    },
    
    getStats: () =>
      apiClient.get(API_ENDPOINTS.ADMIN.STATS),
    
    getAnalytics: (params?: Record<string, any>) => {
      const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
      return apiClient.get(`${API_ENDPOINTS.ADMIN.ANALYTICS}${queryString}`);
    },
  },
};

// Error handling utilities
export const handleApiError = (error: any): string => {
  if (typeof error === 'string') return error;
  if (error?.message) return error.message;
  if (error?.error) return error.error;
  return 'An unexpected error occurred';
};

export const isApiError = (response: ApiResponse): response is ApiResponse & { error: string } => {
  return !response.success && Boolean(response.error);
};
